import React, { useEffect, useState } from 'react';
import { winnerDisplayName } from '../../../utils/winnerName';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { PurchasePackagesType } from '../../../types/types';
import { GiveawayNotification, PreviousGiveawayTable } from './components';
import { useNetwork } from '../../../utils/network';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShowSnackbar } from '../../../utils/snackbar';
import { PackageDisplay } from '../../../components/PackageDisplay';
import { useUser } from '../../../contexts/UserContext';
import { LastWinner } from '../../../types/types';

type GiveawayProps = {
  handlePurchaseSelection: (purchasePackage: PurchasePackagesType) => void;
};

export const Giveaway = ({ handlePurchaseSelection }: GiveawayProps) => {
  const { user, setUser } = useUser();
  const { get } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [giveawayPacks, setGiveawayPacks] = useState([]);
  const [giveawayStats, setGiveawayStats] = useState<{
    completedGiveaways: number;
    totalGivenAwayAud: number;
  } | null>(null);
  const [lastWinner, setLastWinner] = useState<LastWinner | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const data = await get('/package/standalone', true);
        setGiveawayPacks(data);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      }
    };

    const getUserGiveawayEntries = async () => {
      try {
        const result = await get('user/get-user-giveaway-entries', true);
        setUser((currentUser) => ({
          ...currentUser,
          entries: result.entries,
          stackingEntries: result.stackingEntries,
          pendingJackpot: result.pendingJackpot || []
        }));
      } catch (error: any) {
        console.log(error);
      }
    };

    const getGiveawayStats = async () => {
      try {
        const result = await get('/giveaway/stats', true);
        setGiveawayStats({
          completedGiveaways: Number(result?.completedGiveaways || 0),
          totalGivenAwayAud: Number(result?.totalGivenAwayAud) || 0
        });
      } catch (error) {
        console.log('Failed to fetch giveaway stats', error);
      }
    };

    const getLastWinner = async () => {
      try {
        setLastWinner(await get('/giveaway/last-winner', true));
      } catch (error) {
        console.log('Failed to fetch last giveaway winner', error);
        setLastWinner(null);
      }
    };

    fetchPacks();
    getUserGiveawayEntries();
    getGiveawayStats();
    getLastWinner();
  }, []);

  // Handle payment success feedback
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentSuccess = searchParams.get('payment_success');
    const packageName = searchParams.get('package_name');
    const packageCards = searchParams.get('package_cards');
    const packageEntries = searchParams.get('package_entries');
    const bump1 = searchParams.get('bump1') === 'true';
    const bump2 = searchParams.get('bump2') === 'true';

    if (paymentSuccess === 'true' && packageName) {
      let totalCards = parseInt(packageCards || '0');
      let totalEntries = parseInt(packageEntries || '0');

      if (bump1) {
        totalCards += parseInt(packageCards || '0');
        totalEntries += parseInt(packageEntries || '0');
      }

      if (bump2) {
        totalCards += parseInt(packageCards || '0');
        totalEntries += parseInt(packageEntries || '0');
      }

      showSnackbar(
        `🎉 Payment successful! You received ${totalCards} cards and ${totalEntries} giveaway entries for ${decodeURIComponent(packageName)}!`,
        'success'
      );

      // Clean up URL
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, showSnackbar]);

  const totalGivenAway = giveawayStats?.totalGivenAwayAud ?? 0;
  const completedGiveaways = giveawayStats?.completedGiveaways ?? 0;
  const winnerName = lastWinner
    ? winnerDisplayName(
        lastWinner.firstName,
        lastWinner.lastInitial,
        lastWinner.username
      )
    : '';
  const compactAud = (n: number) =>
    `A$${n.toLocaleString('en-AU', {
      notation: 'compact',
      maximumFractionDigits: 2
    })}`;
  const fullAud = (n: number) =>
    `A$${n.toLocaleString('en-AU', {
      maximumFractionDigits: 0
    })}`;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1, pb: 2 }}>
        <Typography
          sx={{
            font: '800 10px ui-monospace, Menlo, Consolas, monospace',
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: '#ffcf40'
          }}
        >
          Giveaways
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            fontWeight: 900,
            fontSize: 'clamp(24px, 3.4vw, 36px)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: '#e8f4fb'
          }}
        >
          Win big{' '}
          <Box component="span" sx={{ color: '#38e8ff' }}>
            every week
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            font: '600 13px/1.55 ui-monospace, Menlo, Consolas, monospace',
            color: '#8ea3b3',
            maxWidth: 640
          }}
        >
          Buy packs for digital assets and entries into the $10,000 jackpot draw,
          plus the weekly member giveaways.
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1.5, md: 2 } }}>
        <GiveawayNotification />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 2,
          flexWrap: 'wrap',
          mt: 2,
          mx: { xs: 1.5, md: 2 }
        }}
      >
        {/* last giveaway winner — its own card, on the left (mirrors the dashboard) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            flex: { xs: '1 1 100%', sm: '1 1 0' },
            minWidth: { xs: '100%', sm: 300 },
            px: { xs: 3, md: 3.5 },
            py: 3,
            background:
              'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
            border: '1px solid rgba(56, 232, 255, 0.22)',
            borderRadius: '16px',
            boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)'
          }}
        >
          <Typography
            sx={{
              font: '800 10px ui-monospace, Menlo, Consolas, monospace',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#8ea3b3',
              mb: 1.5
            }}
          >
            Last winner
          </Typography>
          <Typography
            sx={{
              font: '900 clamp(30px, 4vw, 42px) ui-monospace, Menlo, Consolas, monospace',
              lineHeight: 1,
              color: '#ffcf40',
              textShadow: '0 0 30px rgba(255, 207, 64, 0.25)'
            }}
          >
            {winnerName || '—'}
          </Typography>
          {lastWinner?.username && (
            <Typography
              sx={{
                mt: 1,
                font: '700 12px ui-monospace, Menlo, Consolas, monospace',
                color: '#e8f4fb'
              }}
            >
              username: {lastWinner.username}
            </Typography>
          )}
          {lastWinner?.discordUsername && (
            <Typography
              sx={{
                mt: 0.25,
                font: '700 10px ui-monospace, Menlo, Consolas, monospace',
                color: '#a06fff'
              }}
            >
              Discord: {lastWinner.discordUsername}
            </Typography>
          )}
        </Box>

        {giveawayStats && (
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              flex: { xs: '1 1 100%', sm: '2 1 0' },
              minWidth: { xs: '100%', sm: 280 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 4, sm: 8 },
              flexWrap: 'wrap',
              textAlign: 'center',
              px: { xs: 3, md: 4 },
              py: 3,
              background:
                'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
              border: '1px solid rgba(56, 232, 255, 0.22)',
              borderRadius: '16px',
              boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '0 auto auto 0',
                width: 320,
                height: 160,
                pointerEvents: 'none',
                background:
                  'radial-gradient(420px 200px at 0 0, rgba(56, 232, 255, 0.08), transparent 65%)'
              }
            }}
          >
            <Box>
              <Typography
                title={fullAud(totalGivenAway)}
                sx={{
                  font: '900 clamp(30px, 4vw, 42px) ui-monospace, Menlo, Consolas, monospace',
                  lineHeight: 1,
                  color: '#ffcf40',
                  fontVariantNumeric: 'tabular-nums',
                  textShadow: '0 0 30px rgba(255, 207, 64, 0.25)'
                }}
              >
                {compactAud(totalGivenAway)}
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  font: '800 10px ui-monospace, Menlo, Consolas, monospace',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: '#8ea3b3'
                }}
              >
                Given away
              </Typography>
            </Box>
            <Box
              sx={{
                width: '1px',
                alignSelf: 'stretch',
                minHeight: 56,
                background: 'rgba(140, 170, 190, 0.16)',
                display: { xs: 'none', sm: 'block' }
              }}
            />
            <Box>
              <Typography
                sx={{
                  font: '900 clamp(30px, 4vw, 42px) ui-monospace, Menlo, Consolas, monospace',
                  lineHeight: 1,
                  color: '#38e8ff',
                  fontVariantNumeric: 'tabular-nums',
                  textShadow: '0 0 30px rgba(56, 232, 255, 0.28)'
                }}
              >
                {completedGiveaways.toLocaleString('en-AU')}
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  font: '800 10px ui-monospace, Menlo, Consolas, monospace',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: '#8ea3b3'
                }}
              >
                Giveaways completed
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ my: '3rem', px: { xs: 1, md: 2 } }}>
        <Grid
          container
          rowSpacing={4}
          sx={{ width: '100%', maxWidth: '100%', m: 0 }}
        >
          {giveawayPacks?.map((pack, index) => (
            <Grid xs={12} sm={6} md={4} key={index}>
              <Box sx={{ px: { xs: 0, sm: 1 } }}>
                <PackageDisplay
                  packageData={pack}
                  handlePurchaseSelection={handlePurchaseSelection}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* <PreviousGiveawayTable previousTableData={} /> */}
    </Box>
  );
};
